// プレイヤーの一覧と管理（キック・BAN・OP・ホワイトリスト）。操作はコンソールコマンドとして実行し、監査ログに残る。
import { useState, type FormEvent, type ReactNode } from 'react';
import { apiM } from '../api/client-maint';
import type { ServerView } from '../api/types';
import type { PlayerAction, PlayersView } from '../api/types-maint';
import { errorMessage, useApp } from '../app-context';
import { Dialog, Empty, ErrorAlert } from '../components/ui';
import { formatDateTime } from '../format';
import { usePoll } from '../hooks/usePoll';

const NAME = /^[A-Za-z0-9_]{1,16}$/;

/** banned-players.json の日時（"2026-10-04 21:10:00 +0900"）を表示用にする */
function banDate(value: string | undefined): string {
  if (!value) return '—';
  const iso = value.replace(/^(\S+) (\S+) ([+-]\d\d)(\d\d)$/, '$1T$2$3:$4');
  return Number.isNaN(Date.parse(iso)) ? value : formatDateTime(iso);
}

/** 理由を入れて実行する操作（キック・BAN） */
type ReasonAction = { action: 'kick' | 'ban' | 'network_ip_ban'; player: string } | { action: 'ban_ip'; ip: string };

function ReasonDialog({
  target,
  onClose,
  onRun,
  onNetworkBan,
}: {
  target: ReasonAction;
  onClose: () => void;
  onRun: (action: PlayerAction) => Promise<void>;
  onNetworkBan: (player: string, reason?: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const who = target.action === 'ban_ip' ? target.ip : target.player;
  const title = { kick: 'キック', ban: 'BAN', ban_ip: 'IP を BAN', network_ip_ban: 'IP で BAN（全サーバー）' }[
    target.action
  ];
  const run = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = reason.trim() || undefined;
    if (target.action === 'network_ip_ban') return onNetworkBan(target.player, trimmed);
    await onRun(
      target.action === 'ban_ip'
        ? { action: 'ban_ip', ip: target.ip, reason: trimmed }
        : { action: target.action, player: target.player, reason: trimmed },
    );
  };
  return (
    <Dialog
      title={`${who} を${title}`}
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onClose}>
            キャンセル
          </button>
          <button className="btn danger-solid" form="reason-form">
            {title}する
          </button>
        </>
      }
    >
      <form id="reason-form" className="stack" onSubmit={(event) => void run(event)}>
        <div className="field">
          <label htmlFor="reason">理由（任意。本人の画面に表示されます）</label>
          <input
            id="reason"
            className="input"
            maxLength={200}
            value={reason}
            autoFocus
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        {target.action === 'network_ip_ban' ? (
          <p className="small muted" style={{ margin: 0 }}>
            このサーバーに最後にログインしたときの IP を、すべてのサーバーの入口で BAN
            します。同じ回線の人も入れなくなります。解除は「設定」の「IP BAN（全サーバー共通）」から行えます。
          </p>
        ) : (
          target.action !== 'kick' && (
            <p className="small muted" style={{ margin: 0 }}>
              解除はこの画面の「BAN 中」から行えます。
            </p>
          )
        )}
      </form>
    </Dialog>
  );
}

function Section({
  title,
  count,
  children,
  action,
}: {
  title: string;
  count: number;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section
      className="card"
      data-search-id={
        (
          {
            オンライン: 'players',
            OP: 'player-op',
            ホワイトリスト: 'player-whitelist',
            'BAN 中': 'player-ban',
          } as Record<string, string>
        )[title]
      }
    >
      <div className="card-header">
        <h2>
          {title}（{count}）
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function AddByName({ label, onAdd }: { label: string; onAdd: (name: string) => void }) {
  const [name, setName] = useState('');
  const valid = NAME.test(name);
  return (
    <form
      className="row"
      style={{ gap: 6 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        onAdd(name);
        setName('');
      }}
    >
      <input
        className="input"
        style={{ width: 170 }}
        value={name}
        placeholder="プレイヤー名"
        aria-label={`${label}するプレイヤー名`}
        onChange={(e) => setName(e.target.value.trim())}
      />
      <button className="btn small" disabled={!valid}>
        {label}
      </button>
    </form>
  );
}

export function PlayersTab({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const players = usePoll(() => apiM.players(server.id), 10000, [server.id, server.state]);
  const adminAuto = usePoll(() => apiM.adminAuto(server.id), 60000, [server.id]);
  const [reasonFor, setReasonFor] = useState<ReasonAction>();
  const [busy, setBusy] = useState(false);
  const view: PlayersView | undefined = players.data;
  const running = server.state === 'running';

  const run = async (action: PlayerAction) => {
    setBusy(true);
    try {
      const { output } = await apiM.playerAction(server.id, action);
      notify(output || '実行しました');
      setReasonFor(undefined);
      await players.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  const networkBan = async (player: string, reason?: string) => {
    setBusy(true);
    try {
      const { address } = await apiM.banPlayerIp(server.id, player, reason);
      notify(`${player} の IP（${address}）をすべてのサーバーで BAN しました`);
      setReasonFor(undefined);
      await players.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  if (!view) return <ErrorAlert error={players.error} />;
  const ops = new Set(view.ops.map((o) => o.name.toLowerCase()));
  const listed = new Set(view.whitelist.map((w) => w.name.toLowerCase()));
  const canAct = canOperate && running && !busy;
  const offlineNote = !running && (
    <div className="alert info small">
      サーバーが停止中のため、操作はできません（一覧は最後に保存された内容です）。起動すると操作できます。
    </div>
  );

  const toggleAdminAuto = async (enabled: boolean) => {
    setBusy(true);
    try {
      await apiM.setAdminAuto(server.id, enabled);
      await adminAuto.reload();
      notify(enabled ? '管理者を自動で OP・許可リストに入れます' : '管理者の自動登録をやめました');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 900 }}>
      <ErrorAlert error={players.error} />
      {offlineNote}
      {adminAuto.data && (
        <section className="card card-body stack" style={{ gap: 4 }}>
          <label className="check">
            <input
              type="checkbox"
              data-search-id="admin-auto"
              checked={adminAuto.data.enabled}
              disabled={!canOperate || busy}
              onChange={(event) => void toggleAdminAuto(event.target.checked)}
            />
            管理者を自動で OP・許可リストに入れる
          </label>
          <p className="small muted" style={{ margin: 0 }}>
            利用者タブで Minecraft アカウントを登録したオーナー・管理者を、OP（レベル
            4）と許可リストに入れます。管理者でなくなった人は外します。パネルが入れた分だけを扱い、手で入れた人には触りません。止まっているサーバーは次に起動したときに反映します。
          </p>
        </section>
      )}
      <Section
        title="オンライン"
        count={view.online.length}
        action={view.max !== undefined && <span className="small faint">最大 {view.max} 人</span>}
      >
        {view.online.length === 0 ? (
          <Empty>{running ? '接続中のプレイヤーはいません。' : '停止中です。'}</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <tbody>
                {view.online.map((name) => (
                  <tr key={name}>
                    <td>
                      <span className="mono">
                        {name}{' '}
                        {view.discord[name.toLowerCase()]?.name && (
                          <span className="tag">{view.discord[name.toLowerCase()]!.name}</span>
                        )}
                      </span>{' '}
                      {ops.has(name.toLowerCase()) && <span className="tag">OP</span>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {canOperate && (
                        <>
                          {ops.has(name.toLowerCase()) ? (
                            <button
                              className="btn small"
                              disabled={!canAct}
                              onClick={() => void run({ action: 'deop', player: name })}
                            >
                              OP を外す
                            </button>
                          ) : (
                            <button
                              className="btn small"
                              disabled={!canAct}
                              onClick={() => void run({ action: 'op', player: name })}
                            >
                              OP にする
                            </button>
                          )}{' '}
                          <button
                            className="btn small"
                            disabled={!canAct}
                            onClick={() => setReasonFor({ action: 'kick', player: name })}
                          >
                            キック
                          </button>{' '}
                          <button
                            className="btn ghost small danger"
                            disabled={!canAct}
                            onClick={() => setReasonFor({ action: 'ban', player: name })}
                          >
                            BAN
                          </button>{' '}
                          <button
                            className="btn ghost small danger"
                            disabled={!canAct}
                            title="最後にログインしたときの IP を、すべてのサーバーの入口で BAN します"
                            onClick={() => setReasonFor({ action: 'network_ip_ban', player: name })}
                          >
                            IP BAN
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="grid grid-nodes">
        <Section
          title="OP"
          count={view.ops.length}
          action={canAct && <AddByName label="追加" onAdd={(player) => void run({ action: 'op', player })} />}
        >
          {view.ops.length === 0 ? (
            <Empty>OP はいません。</Empty>
          ) : (
            <ul className="plain-list">
              {view.ops.map((op) => (
                <li key={op.name} className="row">
                  <span className="mono">
                    {op.name}{' '}
                    {view.discord[op.name.toLowerCase()]?.name && (
                      <span className="tag">{view.discord[op.name.toLowerCase()]!.name}</span>
                    )}
                  </span>
                  {op.level !== undefined && <span className="small faint">レベル {op.level}</span>}
                  <span className="spacer" />
                  {canOperate && (
                    <button
                      className="btn ghost small"
                      disabled={!canAct}
                      onClick={() => void run({ action: 'deop', player: op.name })}
                    >
                      外す
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section
          title="ホワイトリスト"
          count={view.whitelist.length}
          action={
            canOperate && (
              <label className="check small">
                <input
                  type="checkbox"
                  disabled={!canAct}
                  checked={view.whitelistEnabled ?? false}
                  onChange={(e) => void run({ action: e.target.checked ? 'whitelist_on' : 'whitelist_off' })}
                />
                有効
              </label>
            )
          }
        >
          <div className="card-body stack" style={{ gap: 8 }}>
            {!view.whitelistEnabled && (
              <div className="small faint">
                無効のあいだは誰でも参加できます（リストは有効にしたときに使われます）。
              </div>
            )}
            {canAct && <AddByName label="追加" onAdd={(player) => void run({ action: 'whitelist_add', player })} />}
          </div>
          {view.whitelist.length > 0 && (
            <ul className="plain-list">
              {view.whitelist.map((entry) => (
                <li key={entry.name} className="row">
                  <span className="mono">
                    {entry.name}{' '}
                    {view.discord[entry.name.toLowerCase()]?.name && (
                      <span className="tag">{view.discord[entry.name.toLowerCase()]!.name}</span>
                    )}
                  </span>
                  <span className="spacer" />
                  {canOperate && (
                    <button
                      className="btn ghost small"
                      disabled={!canAct}
                      onClick={() => void run({ action: 'whitelist_remove', player: entry.name })}
                    >
                      外す
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {view.online.some((name) => !listed.has(name.toLowerCase())) && view.whitelistEnabled && (
            <div className="card-body small faint">
              オンラインでもリストに無い人は、次に入り直すと入れなくなります。
            </div>
          )}
        </Section>
      </div>

      <Section
        title="BAN 中"
        count={view.bans.length + view.ipBans.length}
        action={canAct && <AddByName label="BAN" onAdd={(player) => setReasonFor({ action: 'ban', player })} />}
      >
        {view.bans.length + view.ipBans.length === 0 ? (
          <Empty>BAN しているプレイヤーはいません。</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>対象</th>
                  <th>理由</th>
                  <th>日時</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {view.bans.map((ban) => (
                  <tr key={`p:${ban.name}`}>
                    <td className="mono">
                      {ban.name}{' '}
                      {view.discord[ban.name.toLowerCase()]?.name && (
                        <span className="tag">{view.discord[ban.name.toLowerCase()]!.name}</span>
                      )}
                    </td>
                    <td className="small">{ban.reason ?? <span className="faint">—</span>}</td>
                    <td className="small faint">{banDate(ban.created)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {canOperate && (
                        <button
                          className="btn small"
                          disabled={!canAct}
                          onClick={() => void run({ action: 'pardon', player: ban.name })}
                        >
                          解除
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {view.ipBans.map((ban) => (
                  <tr key={`ip:${ban.ip}`}>
                    <td className="mono">
                      {ban.ip} <span className="tag">IP</span>
                    </td>
                    <td className="small">{ban.reason ?? <span className="faint">—</span>}</td>
                    <td className="small faint">{banDate(ban.created)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {canOperate && (
                        <button
                          className="btn small"
                          disabled={!canAct}
                          onClick={() => void run({ action: 'pardon_ip', ip: ban.ip })}
                        >
                          解除
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      <p className="small faint" style={{ margin: 0 }}>
        プレイヤー名はゲーム内の表示名ではなく Minecraft のユーザー名です。操作はすべて監査ログに残ります。
      </p>
      {reasonFor && (
        <ReasonDialog
          target={reasonFor}
          onClose={() => setReasonFor(undefined)}
          onRun={run}
          onNetworkBan={networkBan}
        />
      )}
    </div>
  );
}
